import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import './Backtest.css';
import './Backtest2.css';
import './MemberView.css';
import './NewMemberView.css';
import './Signal.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LOCKED = { tech: 'QQQ', def1: 'GLD', def2: 'IEF', cash: 'BIL' };

const FRIENDLY = {
  QQQ: 'Growth Engine (Nasdaq 100)',
  GLD: 'Defensive (Gold)',
  IEF: 'Defensive (Treasury Bonds)',
  BIL: 'Defensive (Cash Reserve)',
};

const PASSCODE = 'simplesignal';
const SESSION_KEY = 'signal_unlocked_v1';

const PERIODS = {
  '1y': '1 Year',
  '3y': '3 Years',
  '5y': '5 Years',
  '10y': '10 Years',
};

const STATUS_COPY = {
  CALM: 'Calm',
  WATCH: 'Watching',
  ARMED: 'Armed',
  CLOSED: 'Closed',
  OPEN: 'Open',
};

const sanitizeReason = (reason) => {
  if (!reason) return reason;
  let s = String(reason);
  s = s.replace(/\s*\([^)]*\)/g, '');
  s = s.replace(/:\s*([A-Z]{1,6})\s+-?\d+\.\d+\s*[><=]+\s*[A-Z]{1,6}\s+-?\d+\.\d+/g, ': $1');
  return s.trim();
};

const MetricCard = ({ label, value, subValue, color }) => (
  <div className="bt-metric-card">
    <span className="bt-metric-label">{label}</span>
    <span className="bt-metric-value" style={color ? { color } : {}}>{value}</span>
    {subValue && <span className="bt-metric-sub">{subValue}</span>}
  </div>
);

const LegCard = ({ leg, detail, testid }) => (
  <div className={`sig-leg sig-leg--${(leg.status || 'CALM').toLowerCase()}`} data-testid={testid}>
    <div className="sig-leg-top">
      <span className="sig-leg-name">{leg.name}</span>
      <span className="sig-leg-status">{STATUS_COPY[leg.status] || leg.status}</span>
    </div>
    <div className="sig-leg-what">{leg.what}</div>
    <div className="sig-leg-detail">
      {leg.ts_ratio != null && (
        <>
          <span>VIX <strong>{leg.vix}</strong></span>
          <span>Term structure <strong>{leg.ts_ratio}</strong></span>
          {detail && <span>VIX3M <strong>{leg.vix3m}</strong></span>}
        </>
      )}
      {leg.rv_ratio != null && (
        <>
          <span>Volatility <strong>{leg.rv_ratio}x</strong> of normal</span>
          {detail && (
            <>
              <span>10d vol <strong>{leg.rv10}%</strong></span>
              <span>Normal <strong>{leg.rv_baseline}%</strong></span>
              <span>10d return <strong>{leg.ret10_pct}%</strong></span>
            </>
          )}
        </>
      )}
    </div>
  </div>
);

const Signal = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('5y');
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [sortDesc, setSortDesc] = useState(true);

  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const [passcodeInput, setPasscodeInput] = useState('');
  const [gateError, setGateError] = useState('');

  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    const submitted = (passcodeInput || '').trim().toLowerCase().replace(/\s+/g, '');
    if (submitted === PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, '1');
      setUnlocked(true);
      setGateError('');
    } else {
      setGateError('Incorrect passcode. Please try again.');
    }
  };

  const runEngine = async (force = false, p = period) => {
    setLoading(true);
    setError(null);
    try {
      const asOf = new URLSearchParams(window.location.search).get('as_of');
      const asOfQS = asOf ? `&as_of=${asOf}` : '';
      const res = await fetch(`${API_URL}/api/gt3/run?force=${force}&period=${p}${asOfQS}`);
      const data = await res.json();
      if (data.status === 'success') setResults(data);
      else setError(data.message || 'Unable to load the signal right now.');
    } catch (err) {
      setError('Connection error — the signal could not be verified. Do not assume "no news is good news."');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (unlocked) runEngine(); }, [unlocked]); // eslint-disable-line

  const handlePeriodChange = (p) => { setPeriod(p); runEngine(false, p); };

  const formatCurrency = (val) => `$${val?.toLocaleString() || 0}`;
  const positionTagClass = (sym) => {
    if (!sym) return '';
    const s = sym.toUpperCase();
    if (s === LOCKED.tech) return 'bt2-tag-tech';
    if ([LOCKED.def1, LOCKED.def2, LOCKED.cash].includes(s)) return 'bt2-tag-defensive';
    return '';
  };

  if (!unlocked) {
    return (
      <div className="mv-gate-page" data-testid="signal-gate">
        <div className="mv-gate-card">
          <div className="mv-gate-brand">
            <img src="/logo.png" alt="Pro Stock Money Bowl" className="mv-gate-logo" />
            <div className="mv-gate-titles">
              <div className="mv-gate-eyebrow">GRATEFUL TRUST</div>
              <h1 className="mv-gate-title">The Signal</h1>
              <div className="mv-gate-subtitle">Beta Access · QQQ + Consensus Shutter</div>
            </div>
          </div>
          <form className="mv-gate-form" onSubmit={handlePasscodeSubmit}>
            <label className="mv-gate-label" htmlFor="signal-gate-input">Enter beta passcode</label>
            <input
              id="signal-gate-input"
              type="password"
              className="mv-gate-input"
              value={passcodeInput}
              onChange={(e) => { setPasscodeInput(e.target.value); setGateError(''); }}
              placeholder="Passcode"
              autoFocus
              autoComplete="off"
              data-testid="signal-passcode-input"
            />
            {gateError && <div className="mv-gate-error" data-testid="signal-gate-error">{gateError}</div>}
            <button type="submit" className="mv-gate-button" data-testid="signal-passcode-submit">Unlock</button>
          </form>
          <div className="mv-gate-footer">
            Beta access for invited members of the <strong>Grateful Trust Community</strong>.
          </div>
        </div>
      </div>
    );
  }

  const sig = results?.current_signal;
  const shutter = results?.shutter;
  const fresh = results?.data_freshness;
  const isRotate = sig?.action === 'ROTATE';
  const displaySymbol = isRotate ? (sig?.next_target || LOCKED.tech) : (sig?.current_holding || LOCKED.tech);
  const actionVerb = isRotate ? 'Rotating to' : 'Holding';

  return (
    <div className="bt-container mv-page" data-testid="signal-page">
      <div className="bt-header mv-header-stack">
        <div className="sig-eyebrow">GRATEFUL TRUST · BETA</div>
        <h1 className="bt2-title mv-title">The Signal</h1>
        <div className="mv-subtitle">One growth engine. Two independent volatility witnesses. One decision a day.</div>
        <p className="mv-description">
          The Serene System holds a single growth engine — the Nasdaq 100 — while the market&apos;s
          primary trend is intact. It steps aside into gold, Treasury bonds, or cash when that trend
          breaks. On top of that sits the <strong>Consensus Shutter</strong>: two unrelated
          volatility measures that must <em>both</em> agree before the exit is tightened. One alarm
          is not enough. That is deliberate — false alarms are what destroy long-term returns.
        </p>
      </div>

      {fresh && (
        <div className={`sig-freshness ${fresh.stale ? 'sig-freshness--stale' : ''}`} data-testid="signal-freshness">
          {fresh.stale ? (
            <>
              <strong>DATA STALE — DO NOT TRUST THIS SIGNAL.</strong> Latest price {fresh.price_last_date}
              {' '}({fresh.price_age_days}d old), volatility feed {fresh.vol_age_days}d old. Verify manually before acting.
            </>
          ) : (
            <>
              Data verified · prices through <strong>{fresh.price_last_date}</strong> · volatility feed through{' '}
              <strong>{fresh.vix_last_date}</strong>
              {fresh.as_of_frozen ? ` · frozen as of ${fresh.as_of_frozen}` : ''}
            </>
          )}
        </div>
      )}

      {/* CBOE publishes the VIX history file late, often not until the next morning. When
          that happens we complete the session from CBOE's settled 16:15 quote rather than
          reading the shutter on an older session. Disclosed, never silent. */}
      {fresh && !fresh.stale && !fresh.vol_behind_prices
        && (fresh.vol_filled_from_quote || []).length > 0 && (
        <div className="sig-vol-fill" data-testid="signal-vol-filled">
          <strong>Both volatility witnesses are current.</strong>{' '}
          CBOE had not published its{' '}
          {fresh.vol_filled_from_quote.map((f) => f.name).join(' and ')} history file for{' '}
          {fresh.price_last_date} yet, so{' '}
          {fresh.vol_filled_from_quote.length === 1 ? 'that close was' : 'those closes were'}{' '}
          taken from CBOE's own settled 16:15 ET quote:{' '}
          {fresh.vol_filled_from_quote.map((f) => `${f.name} ${f.close}`).join(', ')}. Same
          source, same session, published earlier. The shutter below is reading{' '}
          {fresh.price_last_date}.
        </div>
      )}

      {/* The volatility feed can publish behind prices. When it does, the two witnesses are
          being read on an older session and a reader must be told, not left with CALM. */}
      {fresh && !fresh.stale && fresh.vol_behind_prices && (
        <div className="sig-vol-lag" data-testid="signal-vol-lag">
          <strong>
            The volatility feed is {fresh.vol_lag_sessions} trading
            {fresh.vol_lag_sessions === 1 ? ' day' : ' days'} behind prices.
          </strong>{' '}
          Prices run through {fresh.price_last_date}, but CBOE has only published volatility
          through {fresh.vix_last_date}. Both witnesses below are being read on{' '}
          {fresh.vix_last_date}, so a move in today's session is not in them yet. Treat today's
          shutter reading as provisional and check again once the feed catches up, usually
          later the same evening.
        </div>
      )}

      {sig && (
        <div className="mv-position-wrap" data-testid="signal-today-position">
          <div className={`mv-position-card ${isRotate ? 'mv-position-card--rotate' : ''}`}>
            <div className="mv-position-header">
              <span className="mv-position-label">Today&apos;s Position</span>
              <span className="mv-position-date">as of {sig.as_of_date}</span>
            </div>
            <div className="mv-position-action">{actionVerb} <strong>{displaySymbol}</strong></div>
            <div className="mv-position-sub">{FRIENDLY[displaySymbol] || ''}</div>
          </div>
        </div>
      )}

      {shutter && (
        <div className="sig-shutter" data-testid="signal-shutter-panel">
          <div className="sig-shutter-head">
            <h2>Consensus Shutter</h2>
            <span
              className={`sig-consensus sig-consensus--${(shutter.status || 'OPEN').toLowerCase()}`}
              data-testid="signal-consensus-status"
            >
              {STATUS_COPY[shutter.status] || shutter.status}
            </span>
          </div>
          <p className="sig-plain" data-testid="signal-plain-english">{shutter.plain_english}</p>

          <div className="sig-legs">
            <LegCard leg={shutter.leg_a} detail={showDetail} testid="signal-leg-a" />
            <LegCard leg={shutter.leg_b} detail={showDetail} testid="signal-leg-b" />
            {(() => {
              const g = shutter.price_guard;
              const closed = shutter.consensus_closed;
              // The 50-day line only matters while the shutter is closed. Showing
              // alarm colours when it is open trains people to ignore real alarms.
              const tone = !closed ? 'idle' : (g.above_ema50 ? 'watch' : 'closed');
              const label = !closed
                ? 'Standby'
                : (g.above_ema50 ? 'Holding the line' : 'Line broken');
              return (
                <div className={`sig-leg sig-leg--${tone}`} data-testid="signal-price-guard">
                  <div className="sig-leg-top">
                    <span className="sig-leg-name">Price Guard</span>
                    <span className="sig-leg-status">{label}</span>
                  </div>
                  <div className="sig-leg-what">
                    {closed
                      ? 'Both witnesses are closed — this line is now what decides'
                      : 'Only comes into play once both volatility witnesses close'}
                  </div>
                  <div className="sig-leg-detail">
                    <span>vs 50-day <strong>{g.pct_above_ema50 > 0 ? '+' : ''}{g.pct_above_ema50}%</strong></span>
                    <span>vs 150-day <strong>{g.pct_above_ema150 > 0 ? '+' : ''}{g.pct_above_ema150}%</strong></span>
                    {showDetail && (
                      <>
                        <span>Close <strong>{g.close}</strong></span>
                        <span>EMA50 <strong>{g.ema50}</strong></span>
                        <span>EMA150 <strong>{g.ema150}</strong></span>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="sig-shutter-foot">
            <span>Shutter closed on <strong>{shutter.shutter_days_pct}%</strong> of days in this window</span>
            <span><strong>{shutter.shutter_exits}</strong> fast exits triggered by consensus</span>
            <button
              className="sig-detail-toggle"
              onClick={() => setShowDetail(!showDetail)}
              data-testid="signal-detail-toggle"
            >
              {showDetail ? 'Hide engine detail' : 'Show engine detail'}
            </button>
          </div>
        </div>
      )}

      <div className="bt-actions mv-actions">
        <div className="bt-period-selector" data-testid="signal-period-selector">
          <span className="bt-selector-label">Period:</span>
          {Object.entries(PERIODS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handlePeriodChange(key)}
              className={`bt-period-btn ${period === key ? 'active' : ''}`}
              disabled={loading}
              data-testid={`signal-period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => runEngine(true)}
          disabled={loading}
          className="bt-run-btn"
          data-testid="signal-refresh-btn"
        >
          {loading ? 'Checking…' : 'Refresh Signal'}
        </button>
      </div>

      {loading && (
        <div className="bt-loading" data-testid="signal-loading">
          <div className="bt-spinner"></div>
          <p>Checking the signal…</p>
          <p className="bt-loading-sub">First run may take 30-60 seconds (price + volatility feeds).</p>
        </div>
      )}

      {error && <div className="bt-error" data-testid="signal-error">{error}</div>}

      {results && !loading && (
        <>
          <div className="bt-period" data-testid="signal-period">
            Performance Window: <strong>{results.backtest_period.start}</strong> to{' '}
            <strong>{results.backtest_period.end}</strong> ({results.backtest_period.years} years)
          </div>

          <div className="bt-metrics-section">
            <h2>Strategy Performance</h2>
            <div className="bt-metrics-row" data-testid="signal-strategy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.strategy_metrics.ending_value)} subValue={`from ${formatCurrency(results.strategy_metrics.starting_value)}`} />
              <MetricCard label="Total Return" value={`${results.strategy_metrics.total_return_pct > 0 ? '+' : ''}${results.strategy_metrics.total_return_pct}%`} color={results.strategy_metrics.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.strategy_metrics.cagr}%`} color={results.strategy_metrics.cagr >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="Max Drawdown" value={`-${results.strategy_metrics.max_drawdown}%`} color="#f59e0b" />
              <MetricCard label="Sharpe Ratio" value={results.strategy_metrics.sharpe_ratio} color={results.strategy_metrics.sharpe_ratio >= 1 ? '#22c55e' : '#f59e0b'} />
              <MetricCard label="Total Trades" value={results.strategy_metrics.total_trades} subValue={`${results.strategy_metrics.trades_per_year}/yr · ${results.strategy_metrics.shutter_exits} shutter exits`} color="#06b6d4" />
            </div>
          </div>

          <div className="bt-metrics-section benchmark">
            <h2>S&amp;P 500 Buy-and-Hold Benchmark</h2>
            <div className="bt-metrics-row" data-testid="signal-spy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.spy_benchmark.ending_value)} subValue="from $100,000" />
              <MetricCard label="Total Return" value={`${results.spy_benchmark.total_return_pct > 0 ? '+' : ''}${results.spy_benchmark.total_return_pct}%`} color={results.spy_benchmark.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.spy_benchmark.cagr}%`} />
              <MetricCard label="Max Drawdown" value={`-${results.spy_benchmark.max_drawdown}%`} color="#f59e0b" />
            </div>
          </div>

          {(() => {
            const diff = results.strategy_metrics.total_return_pct - results.spy_benchmark.total_return_pct;
            const outperformed = diff > 0;
            const ddDiff = results.spy_benchmark.max_drawdown - results.strategy_metrics.max_drawdown;
            const ddBetter = ddDiff > 0;
            return (
              <div className={`bt-outperformance ${outperformed ? 'positive' : 'negative'}`} data-testid="signal-outperformance">
                Strategy {outperformed ? 'outperformed' : 'underperformed'} the S&amp;P 500 by{' '}
                <strong>{Math.abs(diff).toFixed(1)} percentage points</strong>{' '}
                {ddBetter ? 'with' : 'but with'} <strong>{Math.abs(ddDiff).toFixed(1)}%</strong>{' '}
                {ddBetter ? 'less' : 'deeper'} drawdown
              </div>
            );
          })()}

          {results.drawdown_context && (
            <div className="nmv-dd-context" data-testid="signal-dd-context">
              <h2>
                {results.drawdown_context.dd_in_progress ? 'Current Pullback (in progress)' : 'Drawdown in Context'}
              </h2>
              <p className="nmv-dd-intro">
                {results.drawdown_context.dd_in_progress ? (
                  <>
                    The headline drawdown above is <strong>still in progress</strong> — it has not yet
                    recovered to a new all-time high. Every number below is <em>as of today</em>.
                  </>
                ) : (
                  <>
                    The -{results.strategy_metrics.max_drawdown}% above was the single worst
                    stretch of the entire period — not what a normal year looks like. Here is the
                    everyday experience instead.
                  </>
                )}
              </p>
              <div className="bt-metrics-row nmv-dd-row">
                <MetricCard label="Typical Year's Deepest Dip" value={results.drawdown_context.typical_year_dip != null ? `-${results.drawdown_context.typical_year_dip}%` : '-'} subValue="what a normal year's worst dip looked like" color="#f59e0b" />
                <MetricCard label="% of Time Near All-Time High" value={`${results.drawdown_context.pct_near_high}%`} subValue="of all days within 5% of a record high" color="#06b6d4" />
              </div>
            </div>
          )}

          <div className="gt2-allocation" data-testid="signal-allocation">
            <h2>Time-in-Asset Allocation</h2>
            <div className="gt2-alloc-row">
              {results.allocation.map((a) => (
                <div key={a.symbol} className={`gt2-alloc-chip ${a.bucket}`} data-testid={`signal-alloc-${a.symbol.toLowerCase()}`}>
                  <span className="gt2-alloc-sym">{a.symbol}</span>
                  <span className="gt2-alloc-pct">{a.pct}%</span>
                  <span className="gt2-alloc-days">{a.days} days · {a.bucket}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bt-chart-section" data-testid="signal-chart">
            <h2>Growth of $100,000: The Signal vs S&amp;P 500</h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={results.equity_curve} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} interval={Math.floor(results.equity_curve.length / 12)} />
                <YAxis stroke="#94a3b8" tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }}
                  formatter={(value, name) => [`${value}%`, name]}
                  labelFormatter={(label) => `Date: ${label}`}
                  itemSorter={(item) => (item.dataKey === 'portfolio_return' ? 0 : 1)}
                />
                <Legend />
                <Line type="monotone" dataKey="portfolio_return" name="The Signal (QQQ + Consensus)" stroke="#06b6d4" strokeWidth={2.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="spy_return" name="S&P 500 Buy & Hold" stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="5 5" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bt-trades-section" data-testid="signal-trades">
            <div className="bt-trades-header">
              <h2>Position Changes ({results.total_trades_list_count} total)</h2>
              {results.trades.length > 15 && (
                <button onClick={() => setShowAllTrades(!showAllTrades)} className="bt-toggle-btn" data-testid="signal-toggle-trades">
                  {showAllTrades ? 'Show Recent 15' : 'Show All'}
                </button>
              )}
            </div>
            <div className="bt-table-wrapper">
              <table className="bt-trades-table">
                <thead>
                  <tr>
                    <th>
                      <button className="nmv-sort-btn" onClick={() => setSortDesc(!sortDesc)} data-testid="signal-sort-toggle">
                        # {sortDesc ? '▼' : '▲'}
                      </button>
                    </th>
                    <th>Date</th>
                    <th>Action</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Exec Price</th>
                    <th>Portfolio Value</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sorted = [...results.trades].sort((a, b) => (sortDesc ? b.trade_number - a.trade_number : a.trade_number - b.trade_number));
                    const visible = showAllTrades ? sorted : sorted.slice(0, 15);
                    return visible.map((trade, idx) => (
                      <tr key={idx}>
                        <td>{trade.trade_number}</td>
                        <td>{trade.date}</td>
                        <td>{trade.action}</td>
                        <td><span className={`bt-position-tag ${positionTagClass(trade.from)}`}>{trade.from}</span></td>
                        <td><span className={`bt-position-tag ${positionTagClass(trade.to)}`}>{trade.to}</span></td>
                        <td>${trade.price?.toFixed(2)}</td>
                        <td>${trade.portfolio_value?.toLocaleString()}</td>
                        <td className="bt2-reason">{showDetail ? trade.reason : sanitizeReason(trade.reason)}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mv-disclaimer" data-testid="signal-disclaimer">
            Beta. Past performance is not a guarantee of future results. Performance shown reflects
            historical backtest results based on adjusted closing prices including dividends. The
            volatility overlay depends on the CBOE VIX3M series, which begins September 2009 — that
            is why this engine&apos;s history starts in 2010 and does not include the 2008 crisis.
            Live execution may vary due to slippage, market conditions, and broker execution timing.
          </div>
        </>
      )}
    </div>
  );
};

export default Signal;
